import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "../../shared/icons/generate-icon";
import { primaryColor, white } from "../../shared/styles/color";
import { fontFamily } from "../../shared/styles/theme";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  desktopDevice,
  largeScreens,
  tabletDevice,
} from "../../shared/styles/sizes";
import UserMenu from "../../pages/Dashboard/UserMenu";
import FreelancerNotificationBell from "../../shared/notification-bell/FreelancerNotificationBell";
import { isActiveVerifiedUser } from "../../../utils/vettingWorkflow";
import { hasSessionToken } from "../../../utils/authSession";

export const PageHeader = (props) => {
  const [sessionTick, setSessionTick] = useState(0);
  useEffect(() => {
    const onUp = () => setSessionTick((n) => n + 1);
    window.addEventListener("liber-user-updated", onUp);
    window.addEventListener("storage", onUp);
    return () => {
      window.removeEventListener("liber-user-updated", onUp);
      window.removeEventListener("storage", onUp);
    };
  }, []);
  const storedUser = JSON.parse(
    localStorage.getItem("userData") || "null"
  );
  void sessionTick;
  // Stale userData without a JWT must not hide Login / Sign up
  const loggedInUser = hasSessionToken(storedUser) ? storedUser : null;
  const freelancerPostCtaLabel =
    props.freelancerPostCtaLabel?.trim() || "Post a Project";
  const navigate = useNavigate();
  const location = useLocation();
  const isHomepageVariant = props?.variant === "homepage";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const isFreelancerVariant = props?.variant === "freelancer";
  const isAuthRoute =
    location.pathname === "/login" || location.pathname === "/sign-up";

  const userType = loggedInUser?.user?.user_type;
  const isFreelancerAccount =
    userType === "freelancers" || userType === "freelancer";
  const isBusinessAccount = userType === "business";
  const hideFreelancerPostCta = !!loggedInUser && isFreelancerAccount;
  const hideAboutUsLink = location.pathname === "/profile";

  const navItems = useMemo(() => {
    if (isHomepageVariant) {
      return [
        { label: "Find Talent", to: "/browse-freelancers" },
        { label: "Find Work", to: "/browse-projects" },
        { label: "How It Works", to: "/how-it-works" },
        { label: "About Us", to: "/about-us" },
      ];
    }
    if (isFreelancerVariant) {
      const items = [];
      if (!isBusinessAccount) {
        items.push({ label: "Project Listings", to: "/browse-projects" });
      }
      if (isFreelancerAccount) {
        const verified = isActiveVerifiedUser(loggedInUser?.user);
        items.push({
          label: verified ? "Verified" : "Get Verified",
          to: "/vetting",
        });
      }
      items.push({ label: "How it works", to: "/how-it-works" });
      if (!hideAboutUsLink) {
        items.push({ label: "About us", to: "/about-us" });
      }
      return items;
    }
    const hideMarketingLinks =
      isAuthRoute || !!loggedInUser || props?.hideMarketingLinks === true;
    const items = [];
    if (!isBusinessAccount) {
      items.push({ label: "Job Listings", to: "/job-listings" });
    }
    items.push(
      { label: "How it works", to: "/how-it-works" },
      { label: "About us", to: "/about-us" }
    );
    return items;
  }, [
    isHomepageVariant,
    isFreelancerVariant,
    isFreelancerAccount,
    isAuthRoute,
    isBusinessAccount,
    loggedInUser,
    props?.hideMarketingLinks,
    hideAboutUsLink,
  ]);

  const headerLinks = navItems.map((item) => (
    <HeaderLink
      key={item.to}
      $marketing={!isHomepageVariant && !isFreelancerVariant}
      $active={location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)}
      onClick={() => {
        navigate(item.to);
      }}
    >
      {item.label}
    </HeaderLink>
  ));

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobileMenuOpen]);

  if (isFreelancerVariant) {
    return (
      <>
        <PageHeaderContainer $freelancer>
          <LogoSlot $freelancer>
            <NavLogoLink to="/home">
              <LogoMark $freelancer>
                <Icon src={"/Logo.png"} style={{ height: "28px", width: "28px" }} />
              </LogoMark>
            </NavLogoLink>
          </LogoSlot>

          <NavCenter $freelancer>
            <LinksContainer $freelancer>
              {navItems.map((item) => (
                <HeaderLink
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  $freelancer
                >
                  {item.label}
                </HeaderLink>
              ))}
            </LinksContainer>
          </NavCenter>

          <HeaderRightSlot $freelancer>
            <DesktopOnly>
              {!hideFreelancerPostCta && (
                <PostProjectButton onClick={() => navigate("/browse-projects")}>
                  {freelancerPostCtaLabel}
                </PostProjectButton>
              )}
              {loggedInUser && isFreelancerAccount && (
                <FreelancerNotificationBell borderColor={primaryColor} />
              )}
              {loggedInUser && <UserMenu />}
            </DesktopOnly>

            <MobileOnly>
              {!loggedInUser ? (
                <MobileAuthInline>
                  <MobileAuthLink $primary onClick={() => navigate("/login")}>Log In</MobileAuthLink>
                  <MobileAuthDivider>/</MobileAuthDivider>
                  <MobileAuthLink onClick={() => navigate("/sign-up")}>Sign Up</MobileAuthLink>
                </MobileAuthInline>
              ) : (
                <>
                  {isFreelancerAccount && (
                    <FreelancerNotificationBell borderColor={primaryColor} />
                  )}
                  <UserMenu />
                </>
              )}
              <HamburgerButton
                type="button"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen((v) => !v)}
              >
                <HamburgerIcon $open={isMobileMenuOpen}>
                  <span />
                  <span />
                  <span />
                </HamburgerIcon>
              </HamburgerButton>
            </MobileOnly>
          </HeaderRightSlot>

          {isMobileMenuOpen && (
            <MobileMenuPanel role="dialog" aria-label="Navigation menu">
              <MobileMenuList>
                {navItems.map((item) => (
                  <MobileMenuItem
                    key={item.to}
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate(item.to);
                    }}
                  >
                    {item.label}
                  </MobileMenuItem>
                ))}
                {!hideFreelancerPostCta && (
                  <MobileMenuItem
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/browse-projects");
                    }}
                  >
                    {freelancerPostCtaLabel}
                  </MobileMenuItem>
                )}
              </MobileMenuList>
            </MobileMenuPanel>
          )}
        </PageHeaderContainer>

        {isMobileMenuOpen && (
          <MobileMenuBackdrop
            aria-hidden="true"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </>
    );
  }

  if (isHomepageVariant) {
    return (
      <>
        <PageHeaderContainer $homepage>
          <LogoSlot>
            <NavLogoLink to="/home">
              <LogoMark $homepage>
                <Icon src={"/Logo.png"} style={{ height: "28px", width: "28px" }} />
              </LogoMark>
              <BrandName>Liber</BrandName>
            </NavLogoLink>
          </LogoSlot>

          <NavCenter $homepage>
            <LinksContainer $homepage>{headerLinks}</LinksContainer>
          </NavCenter>

          <HeaderRightSlot>
            <DesktopOnly>
              {!loggedInUser ? (
                <ButtonContainer>
                  <LoginButton $homepage onClick={() => navigate("/login")}>
                    Sign In
                  </LoginButton>
                  <SignUpButton $homepage onClick={() => navigate("/sign-up")}>
                    Sign up
                  </SignUpButton>
                </ButtonContainer>
              ) : (
                <HomepageAuthRow>
                  {!isFreelancerAccount && (
                    <HomePostProjectButton
                      type="button"
                      onClick={() => navigate("/add-project")}
                    >
                      Post a Project
                    </HomePostProjectButton>
                  )}
                  <UserMenu />
                </HomepageAuthRow>
              )}
            </DesktopOnly>

            <MobileOnly>
              {!loggedInUser ? (
                <MobileAuthInline>
                  <MobileAuthLink
                    type="button"
                    onClick={() => navigate("/login")}
                  >
                    Sign In
                  </MobileAuthLink>
                  <MobileSignUpBtn
                    type="button"
                    onClick={() => navigate("/sign-up")}
                  >
                    Sign up
                  </MobileSignUpBtn>
                </MobileAuthInline>
              ) : (
                <UserMenu />
              )}

              <HamburgerButton
                type="button"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen((v) => !v)}
              >
                <HamburgerIcon $open={isMobileMenuOpen}>
                  <span />
                  <span />
                  <span />
                </HamburgerIcon>
              </HamburgerButton>
            </MobileOnly>
          </HeaderRightSlot>

          {isMobileMenuOpen && (
            <MobileMenuPanel role="dialog" aria-label="Navigation menu">
              <MobileMenuList>
                {navItems.map((item) => (
                  <MobileMenuItem
                    key={item.to}
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate(item.to);
                    }}
                  >
                    {item.label}
                  </MobileMenuItem>
                ))}
                {loggedInUser && !isFreelancerAccount && (
                  <MobileMenuItem
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/add-project");
                    }}
                  >
                    Post a Project
                  </MobileMenuItem>
                )}
                {!loggedInUser && (
                  <>
                    <MobileMenuItem
                      type="button"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        navigate("/login");
                      }}
                    >
                      Sign In
                    </MobileMenuItem>
                    <MobileMenuItem
                      type="button"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        navigate("/sign-up");
                      }}
                    >
                      Sign up
                    </MobileMenuItem>
                  </>
                )}
              </MobileMenuList>
            </MobileMenuPanel>
          )}
        </PageHeaderContainer>

        {isMobileMenuOpen && (
          <MobileMenuBackdrop
            aria-hidden="true"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PageHeaderContainer $marketing>
        <LogoSlot $marketing>
          <NavLogoLink to="/home">
            <LogoMark $marketing>
              <Icon src={"/Logo.png"} style={{ height: "28px", width: "28px" }} />
            </LogoMark>
          </NavLogoLink>
        </LogoSlot>

        <NavCenter $marketing>
          <LinksContainer $marketing>{headerLinks}</LinksContainer>
        </NavCenter>

        <HeaderRightSlot $marketing>
          <DesktopOnly>
            {!loggedInUser ? (
              <ButtonContainer>
                <SignUpButton onClick={() => navigate("/sign-up")}>Sign up</SignUpButton>
                <LoginButton onClick={() => navigate("/login")}>Login</LoginButton>
              </ButtonContainer>
            ) : (
              <UserMenu />
            )}
          </DesktopOnly>

          <MobileOnly>
            {!loggedInUser ? (
              <MobileAuthInline>
                <MobileAuthLink type="button" onClick={() => navigate("/login")}>
                  Sign In
                </MobileAuthLink>
                <MobileSignUpBtn
                  type="button"
                  onClick={() => navigate("/sign-up")}
                >
                  Sign up
                </MobileSignUpBtn>
              </MobileAuthInline>
            ) : (
              <UserMenu />
            )}

            <HamburgerButton
              type="button"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen((v) => !v)}
            >
              <HamburgerIcon $open={isMobileMenuOpen}>
                <span />
                <span />
                <span />
              </HamburgerIcon>
            </HamburgerButton>
          </MobileOnly>
        </HeaderRightSlot>

        {isMobileMenuOpen && (
          <MobileMenuPanel role="dialog" aria-label="Navigation menu">
            <MobileMenuList>
              {navItems.map((item) => (
                <MobileMenuItem
                  key={item.to}
                  type="button"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate(item.to);
                  }}
                >
                  {item.label}
                </MobileMenuItem>
              ))}
            </MobileMenuList>
          </MobileMenuPanel>
        )}
      </PageHeaderContainer>

      {isMobileMenuOpen && (
        <MobileMenuBackdrop aria-hidden="true" onClick={() => setIsMobileMenuOpen(false)} />
      )}
    </>
  );
};

const NavLogoLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

export const LinksAndIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

export const HeaderLink = styled.li`
  list-style: none;
  cursor: pointer;
  font-family: ${fontFamily.font};
  font-weight: ${(props) => (props.$active ? 600 : 400)};
  font-size: ${(props) =>
    props.$freelancer || props.$marketing ? "16px" : "18px"};
  line-height: 22px;
  white-space: nowrap;
  color: ${(props) =>
    props.$active ? primaryColor : props.$freelancer ? "#1d1e2b" : "#2a2a2a"};
  padding-bottom: 4px;
  border-bottom: ${(props) =>
    props.$active ? `2px solid ${primaryColor}` : "2px solid transparent"};
`;

const PostProjectButton = styled.button`
  border-radius: 100px;
  padding: 12px 20px;
  background-color: ${primaryColor};
  color: ${white};
  border: 0;
  cursor: pointer;
  font-family: ${fontFamily.font};
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const BrandName = styled.span`
  font-family: ${fontFamily.font};
  font-weight: 700;
  font-size: 22px;
  color: ${primaryColor};
`;

const HomepageAuthRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const HomePostProjectButton = styled.button`
  border-radius: 100px;
  padding: 10px 18px;
  background: transparent;
  color: ${primaryColor};
  border: 1.5px solid ${primaryColor};
  cursor: pointer;
  font-family: ${fontFamily.font};
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${primaryColor};
    color: ${white};
  }
`;

export const LinksContainer = styled.ul`
  /* Mobile */
  display: none;

  @media (min-width: ${tabletDevice}px) {
    display: none;
  }

  @media (min-width: ${desktopDevice}px) {
    display: flex;
    gap: ${(props) =>
      props.$homepage || props.$freelancer
        ? "32px"
        : props.$marketing
          ? "22px"
          : "20px"};
    align-items: center;
    justify-content: ${(props) =>
      props.$homepage || props.$freelancer || props.$marketing
        ? "center"
        : "flex-start"};
    margin: 0;
    padding: 0;
    flex-wrap: nowrap;
  }

  @media (min-width: ${largeScreens}px) {
    display: flex;
    gap: ${(props) =>
      props.$homepage || props.$freelancer
        ? "32px"
        : props.$marketing
          ? "28px"
          : "20px"};
    align-items: center;
    justify-content: ${(props) =>
      props.$homepage || props.$freelancer || props.$marketing
        ? "center"
        : "flex-start"};
    margin: 0;
    padding: 0;
    flex-wrap: nowrap;
  }
`;

export const ButtonContainer = styled.div`
  /* Mobile */
  display: none;

  @media (min-width: ${tabletDevice}px) {
    display: none;
  }

  @media (min-width: ${desktopDevice}px) {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-shrink: 0;
  }

  @media (min-width: ${largeScreens}px) {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-shrink: 0;
  }
`;

export const HomePageButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
`;

export const PageHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-bottom: ${(props) =>
    props.$homepage || props.$freelancer ? "none" : `1px solid ${primaryColor}`};
  padding: ${(props) =>
    props.$homepage
      ? "16px 16px"
      : props.$freelancer
        ? "16px 24px"
        : props.$marketing
          ? "16px 24px"
          : "20px"};
  border-radius: ${(props) =>
    props.$homepage || props.$freelancer ? "0 0 14px 14px" : "0"};
  box-shadow: ${(props) =>
    props.$homepage || props.$freelancer
      ? "2px 5px 12px rgba(101, 104, 104, 0.42)"
      : "none"};
  position: sticky;
  top: 0;
  background: ${white};
  z-index: 10000;
  box-sizing: border-box;
  width: 100%;
  overflow: visible;

  @media (min-width: ${desktopDevice}px) {
    padding: ${(props) =>
      props.$homepage
        ? "18px 32px"
        : props.$freelancer
          ? "18px 32px"
          : props.$marketing
            ? "18px 32px"
            : "20px"};
  }

  /* Marketing pages: logo | centered nav | auth actions */
  ${(props) =>
    props.$marketing
      ? `
    @media (min-width: ${desktopDevice}px) {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
      align-items: center;
      column-gap: 20px;
    }
  `
      : ""}

  /* Dashboard header (matches Figma top bar) */
  ${(props) =>
    props.$dashboard
      ? `
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    border-bottom: 1px solid #d4d4d4;
    box-shadow: none;
    border-radius: 0 0 14px 14px;
    padding: 12px 20px;
    @media (min-width: ${desktopDevice}px) { padding: 12px 24px; }
  `
      : ""}

  /* Freelancer: true center nav on desktop — equal side columns */
  ${(props) =>
    props.$freelancer
      ? `
    @media (min-width: ${desktopDevice}px) {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
      align-items: center;
      column-gap: 16px;
    }
  `
      : ""}
`;

const HeaderRightSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  ${(props) =>
    props.$freelancer || props.$marketing
      ? `
    @media (min-width: ${desktopDevice}px) {
      justify-self: end;
    }
  `
      : ""}
`;

const DesktopOnly = styled.div`
  display: none;

  @media (min-width: ${desktopDevice}px) {
    display: flex;
    align-items: center;
    gap: 20px;
  }
`;

const MobileOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (min-width: ${desktopDevice}px) {
    display: none;
  }
`;

const MobileAuthInline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const MobileAuthLink = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.$primary ? primaryColor : "#1d1e2b")};
`;

const MobileSignUpBtn = styled.button`
  border: 0;
  background: ${primaryColor};
  color: ${white};
  font-family: ${fontFamily.font};
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.92;
  }
`;

const MobileAuthDivider = styled.span`
  color: #1d1e2b;
  font-weight: 600;
`;

const HamburgerButton = styled.button`
  border: 0;
  background: transparent;
  padding: 8px;
  margin: 0;
  cursor: pointer;
  border-radius: 10px;

  :focus-visible {
    outline: 2px solid ${primaryColor};
    outline-offset: 3px;
  }
`;

const HamburgerIcon = styled.div`
  width: 22px;
  height: 18px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  span {
    display: block;
    height: 2px;
    width: 100%;
    background: ${primaryColor};
    border-radius: 999px;
    transition: transform 160ms ease, opacity 160ms ease;
    transform-origin: center;
  }

  ${(props) =>
    props.$open
      ? `
    span:nth-child(1) { transform: translateY(8px) rotate(45deg); }
    span:nth-child(2) { opacity: 0; }
    span:nth-child(3) { transform: translateY(-8px) rotate(-45deg); }
  `
      : ""}
`;

const MobileMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 12px;
  left: 12px;
  background: ${white};
  border: 1px solid #e6eaed;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  padding: 10px;
  z-index: 10001;

  @media (min-width: ${desktopDevice}px) {
    display: none;
  }
`;

const MobileMenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileMenuItem = styled.button`
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 12px 12px;
  border-radius: 12px;
  font-family: ${fontFamily.font};
  font-size: 16px;
  font-weight: 500;
  color: #1d1e2b;

  :hover {
    background: #f4f5f6;
  }

  :focus-visible {
    outline: 2px solid ${primaryColor};
    outline-offset: 2px;
  }
`;

const MobileMenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
  z-index: 9999;

  @media (min-width: ${desktopDevice}px) {
    display: none;
  }
`;

export const SignUpButton = styled.button`
  width: ${(props) => (props.$homepage ? "108px" : "93px")};
  // height: 43px;
  border-radius: 100px;
  padding: 12px 16px 12px 16px;
  background-color: ${primaryColor};
  color: ${white};
  border: 0;
  cursor: pointer;
  font-family: ${fontFamily.font};
  font-size: 16px;
  font-weight: 500;

`;

export const LoginButton = styled.button`
  background-color: transparent;
  border: 0;
  cursor: pointer;
  font-family: ${fontFamily.font};
  font-size: ${(props) => (props.$homepage ? "20px" : "16px")};
  font-weight: ${(props) => (props.$homepage ? "500" : "400")};
  color: #1d1e2b;
`;

const LogoMark = styled.div`
  width: ${(props) =>
    props.$homepage || props.$freelancer || props.$marketing ? "42px" : "40px"};
  height: ${(props) =>
    props.$homepage || props.$freelancer || props.$marketing ? "42px" : "40px"};
  border: ${(props) =>
    props.$homepage || props.$freelancer || props.$marketing
      ? `2px solid ${primaryColor}`
      : "none"};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  ${(props) =>
    props.$freelancer || props.$marketing
      ? `
    @media (min-width: ${desktopDevice}px) {
      justify-self: start;
    }
  `
      : ""}
`;

const NavCenter = styled.nav`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;

  ${(props) =>
    props.$homepage || props.$marketing
      ? `
    @media (max-width: ${desktopDevice - 1}px) {
      display: none;
    }
  `
      : ""}

  ${(props) =>
    props.$freelancer || props.$marketing
      ? `
    @media (min-width: ${desktopDevice}px) {
      flex: none;
      justify-self: center;
      width: max-content;
      max-width: 100%;
    }
  `
      : ""}
`;
